import { useQuery } from "@apollo/client";
import moment from "moment";
import { useState } from "react";
import {
  GetProfileWeeklySummaryQuery,
  GetProfileWeeklySummaryQueryVariables,
} from "types/graphql-schema";

import ToggleChevronIcon from "@components/layout/toggle-chevron-icon";

import getProfileWeeklySummaryQuery from "../graphql/get-profile-weekly-summary-query";
import SummaryLoading from "./summary-loading";
import SummarySection from "./summary-section";

const WEEK_LABELS: Record<number, string> = { 0: "This Week", 1: "Last Week" };

// "Jan 14 → 19" when the range is within one month, else "Jan 14 → Feb 2".
const formatWeekRange = (weekStart: string, weekEnd: string): string => {
  const start = moment(weekStart);
  const end = moment(weekEnd);
  const endFormat = start.month() === end.month() ? "D" : "MMM D";
  return `${start.format("MMM D")} → ${end.format(endFormat)}`;
};

// "Maya's" from "Maya Patel"; falls back to "Their" when the name is missing.
const firstNamePossessive = (name: string): string => {
  const first = (name || "").trim().split(/\s+/)[0];
  return first ? `${first}'s` : "Their";
};

const SummaryWeekBlock = ({
  userId,
  weekOffset,
}: {
  userId: number;
  weekOffset: number;
}) => {
  const [expanded, setExpanded] = useState(true);
  const { data, loading, error } = useQuery<
    GetProfileWeeklySummaryQuery,
    GetProfileWeeklySummaryQueryVariables
  >(getProfileWeeklySummaryQuery, {
    variables: { userId, weekOffset },
  });

  const summary = data?.profileWeeklySummary;
  const label = WEEK_LABELS[weekOffset];
  const range = summary
    ? formatWeekRange(summary.weekStart, summary.weekEnd)
    : "";
  const isEmpty =
    !!summary && summary.insights.length === 0 && summary.activity.length === 0;

  return (
    <div className="flex flex-col gap-5">
      <button
        onClick={() => setExpanded((value) => !value)}
        className="flex w-fit items-center gap-2"
      >
        {label && (
          <span className="text-xl font-bold text-gray-900">{label}</span>
        )}
        {range && (
          <span className="text-xl font-bold text-blue-600">{range}</span>
        )}
        <ToggleChevronIcon expanded={expanded} />
      </button>

      {expanded &&
        (loading ? (
          <SummaryLoading />
        ) : error || !summary ? (
          <div className="text-sm text-gray-500">
            Couldn&apos;t load this week&apos;s summary.
          </div>
        ) : isEmpty ? (
          <div
            aria-label="Summary empty state"
            className="rounded-lg border border-dashed border-gray-200 p-6 text-center text-sm text-gray-400"
          >
            No tracked activity this week.
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            <SummarySection
              title="Insights"
              lines={summary.insights}
              variant="insight"
            />
            <SummarySection
              title={`${firstNamePossessive(summary.subjectName)} Activity`}
              lines={summary.activity}
              variant="activity"
            />
          </div>
        ))}
    </div>
  );
};

export default SummaryWeekBlock;
